import { useEffect, useState } from 'react';
import api from '../services/api';

export interface WorkStatus {
  id: string;
  label: string;
  color: string;
}

// ── Module-scoped shared cache ────────────────────────────────────────────────
// One fetch shared across every consumer (ticket dropdown + badges). Subscribers
// are notified on refresh so dropdowns update live when admins edit the list.
let _cache: WorkStatus[] | null = null;
let _inflight: Promise<WorkStatus[]> | null = null;
const _subscribers = new Set<(w: WorkStatus[]) => void>();

async function fetchWorkStatuses(): Promise<WorkStatus[]> {
  if (_inflight) return _inflight;
  _inflight = api
    .get<{ workStatuses: WorkStatus[] }>('/work-statuses')
    .then(res => {
      _cache = res.data.workStatuses ?? [];
      _inflight = null;
      _subscribers.forEach(fn => fn(_cache!));
      return _cache;
    })
    .catch(err => { _inflight = null; throw err; });
  return _inflight;
}

/** Force a refetch and notify all mounted consumers (call after admin edits). */
export function invalidateWorkStatuses(): void {
  _cache = null;
  fetchWorkStatuses().catch(() => { /* consumers keep their last list */ });
}

export function useWorkStatuses() {
  const [statuses, setStatuses] = useState<WorkStatus[]>(_cache ?? []);
  const [loading, setLoading] = useState(_cache === null);

  useEffect(() => {
    const onUpdate = (w: WorkStatus[]) => setStatuses(w);
    _subscribers.add(onUpdate);

    if (_cache) {
      setStatuses(_cache);
      setLoading(false);
    } else {
      fetchWorkStatuses().then(() => setLoading(false)).catch(() => setLoading(false));
    }

    return () => { _subscribers.delete(onUpdate); };
  }, []);

  return { statuses, loading, refresh: invalidateWorkStatuses };
}

/** Look up a color for a work-status label (neutral gray fallback). */
export function workStatusColor(statuses: WorkStatus[], label?: string | null): string {
  if (!label) return '#6B7280';
  return statuses.find(s => s.label === label)?.color ?? '#6B7280';
}
